import { Button, Container, Group, Text, Title } from '@mantine/core';
import { IconRefresh } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { pagesTestIds } from '../testids';
import styles from './ErrorPage.module.css';

/**
 * Shown when the app never finishes loading (TV1-2369).
 *
 * ── Why the existing error pages did not cover this ───────────────────────
 *
 * `ErrorPage` is rendered by `ErrorBoundary`, which only fires when a component
 * THROWS during render. The failure this page is for throws nothing: a boot
 * query never settles and the loader spins indefinitely. No error, no
 * explanation, nothing to click — the app just appears broken while insisting it
 * is working.
 *
 * `ShieldsErrorPage` is the closest relative and does offer a reload, but it is
 * for one diagnosed cause (a browser blocking WebSockets) and says so in its
 * copy. This page makes no claim about the cause, because it does not know one:
 * a stall is the ABSENCE of information.
 *
 * ── Why "Actualizar" is the primary action ────────────────────────────────
 *
 * A reload is the only thing that reliably clears a wedged socket, a stale
 * token, or a half-initialised store, and it is what the user would have done
 * eventually anyway — several minutes later, and annoyed. Going home is the
 * secondary action: it helps if one route's data is the problem, and does
 * nothing if the session itself is stuck.
 */
const StalledLoadPage = () => {
  const { t } = useTranslation( 'error' );

  return (
    <Container className={styles.root} data-testid={pagesTestIds.stalledLoadPage}>
      <div className={styles.label}>Oops...</div>
      <Title className={styles.title}>
        {t( 'stalled_load_title', 'No pudimos cargar la aplicación' )}
      </Title>
      <Text c="dimmed" size="lg" ta="center" className={styles.description}>
        {t(
          'stalled_load_message',
          'La carga está tardando más de lo normal. Puede ser un problema de conexión o del servidor.'
        )}
      </Text>
      <Text c="dimmed" size="md" ta="center" className={styles.description}>
        {t(
          'stalled_load_hint',
          'Actualizá la página para volver a intentar. Si el problema sigue, esperá unos minutos y probá de nuevo.'
        )}
      </Text>

      <Group justify="center">
        <Button
          size="md"
          radius="xs"
          leftSection={<IconRefresh size={16} />}
          onClick={() => window.location.reload()}
          data-testid={pagesTestIds.stalledLoadRetry}
        >
          {t( 'stalled_load_retry', 'Actualizar' )}
        </Button>
      </Group>
    </Container>
  );
};

export default StalledLoadPage;
